/**
 * Modern Drag & Drop File Upload Component
 * Replaces active_storage_drag_and_drop gem with HTML5 + Vanilla JS
 */
class DragDropUpload {
  constructor(element, options = {}) {
    this.element = element;
    this.options = {
      sizeLimit: options.sizeLimit || window.MAX_FILE_SIZE_BYTES || 100000000, // Use global constant or fallback
      multiple: options.multiple !== false,
      directUpload: options.directUpload === true, // Default to false for testing
      acceptedTypes: options.acceptedTypes || null,
      ...options
    };
    
    this.fileInput = this.element.querySelector('input[type="file"]');
    this.dropZone = this.element.querySelector('.drop-zone');
    this.fileList = this.element.querySelector('.file-list');
    this.progressContainer = this.element.querySelector('.progress-container');
    
    this.init();
  }

  init() {
    this.bindEvents();
    this.setupDropZone();
  }

  bindEvents() {
    // Check if elements exist
    if (!this.fileInput || !this.dropZone) {
      console.error('DragDropUpload: Required elements not found', {
        fileInput: this.fileInput,
        dropZone: this.dropZone
      });
      return;
    }

    // File input change - handle click-selected files
    this.fileInput.addEventListener('change', (e) => {
      this.handleClickSelectedFiles(e.target.files);
    });

    // Drop zone click
    this.dropZone.addEventListener('click', (e) => {
      e.preventDefault();
      this.fileInput.click();
    });

    // Drag and drop events
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      this.dropZone.addEventListener(eventName, (e) => this.preventDefaults(e), false);
      document.body.addEventListener(eventName, (e) => this.preventDefaults(e), false);
    });

    ['dragenter', 'dragover'].forEach(eventName => {
      this.dropZone.addEventListener(eventName, () => this.highlight(), false);
    });

    ['dragleave', 'drop'].forEach(eventName => {
      this.dropZone.addEventListener(eventName, () => this.unhighlight(), false);
    });

    this.dropZone.addEventListener('drop', (e) => this.handleDrop(e), false);
  }

  preventDefaults(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  highlight() {
    this.dropZone.classList.add('dragover');
  }

  unhighlight() {
    this.dropZone.classList.remove('dragover');
  }

  handleDrop(e) {
    const dt = e.dataTransfer;
    const files = dt.files;
    this.handleDroppedFiles(files);
  }

  handleClickSelectedFiles(files) {
    if (!files || files.length === 0) {
      return;
    }

    // For click-selected files, just show them in UI
    // They're already in the file input, so no need to add to customFiles
    Array.from(files).forEach(file => {
      if (this.validateFile(file)) {
        const fileItem = this.createFileItem(file);
        fileItem.classList.add('click-selected'); // Mark as click-selected
        this.fileList.appendChild(fileItem);
      }
    });
  }

  handleDroppedFiles(files) {
    if (!files || files.length === 0) {
      return;
    }

    try {
      const validFiles = this.validateFiles(files);
      
      if (validFiles.length > 0) {
        this.uploadFiles(validFiles);
      }
    } catch (error) {
      console.error('Error handling dropped files:', error);
      this.showError('ファイル処理中にエラーが発生しました。');
    }
  }

  setupDropZone() {
    this.dropZone.classList.add('drag-drop-zone');
  }

  handleFiles(files) {
    if (!files || files.length === 0) {
      return;
    }

    try {
      const validFiles = this.validateFiles(files);
      
      if (validFiles.length > 0) {
        this.uploadFiles(validFiles);
      }
    } catch (error) {
      console.error('Error handling files:', error);
      this.showError('ファイル処理中にエラーが発生しました。');
    }
  }

  validateFiles(files) {
    const validFiles = [];
    Array.from(files).forEach(file => {
      if (this.validateFile(file)) {
        validFiles.push(file);
      }
    });
    return validFiles;
  }

  validateFile(file) {
    // Size validation
    if (file.size > this.options.sizeLimit) {
      this.showError(`ファイル "${file.name}" のサイズが上限（${this.formatFileSize(this.options.sizeLimit)}）を超えています。`);
      return false;
    }

    // Type validation (if specified)
    if (this.options.acceptedTypes && !this.options.acceptedTypes.includes(file.type)) {
      this.showError(`ファイル "${file.name}" の形式がサポートされていません。`);
      return false;
    }

    return true;
  }

  async uploadFiles(files) {
    for (const file of files) {
      try {
        await this.uploadFile(file);
      } catch (error) {
        console.error('Error uploading file:', file.name, error);
        this.showError(`ファイル "${file.name}" のアップロードに失敗しました。`);
      }
    }
  }

  async uploadFile(file) {
    const fileItem = this.createFileItem(file);
    this.fileList.appendChild(fileItem);

    try {
      if (this.options.directUpload) {
        try {
          await this.directUpload(file, fileItem);
        } catch (directUploadError) {
          console.warn('Direct upload failed, falling back to form upload:', directUploadError);
          this.showError(`直接アップロードに失敗しました。フォーム送信時にアップロードされます。`);
          this.addToFormData(file, fileItem);
        }
      } else {
        this.addToFormData(file, fileItem);
      }
    } catch (error) {
      console.error('Error in uploadFile:', error);
      fileItem.classList.add('error');
      this.showError(`ファイル "${file.name}" の処理に失敗しました。`);
    }
  }

  createFileItem(file) {
    const fileItem = document.createElement('div');
    fileItem.className = 'file-item';
    fileItem.innerHTML = `
      <div class="file-info">
        <i class="fa fa-file-o"></i>
        <span class="file-name">${file.name}</span>
        <span class="file-size">(${this.formatFileSize(file.size)})</span>
        <button type="button" class="btn-remove" aria-label="ファイルを削除">
          <i class="fa fa-trash-o"></i>
        </button>
      </div>
      <div class="progress-bar" style="display: none;">
        <div class="progress-fill"></div>
        <span class="progress-text">0%</span>
      </div>
    `;

    // Remove button functionality
    const removeBtn = fileItem.querySelector('.btn-remove');
    removeBtn.addEventListener('click', () => {
      this.removeFile(fileItem);
    });

    return fileItem;
  }

  async directUpload(file, fileItem) {
    try {
      const progressBar = fileItem.querySelector('.progress-bar');
      const progressFill = fileItem.querySelector('.progress-fill');
      const progressText = fileItem.querySelector('.progress-text');
      
      progressBar.style.display = 'block';

      // Get direct upload URL from Rails
      const directUploadUrl = await this.getDirectUploadUrl(file);
      
      // Upload to cloud storage
      const blob = await this.uploadToCloud(file, directUploadUrl, (progress) => {
        const percentage = Math.round(progress * 100);
        progressFill.style.width = `${percentage}%`;
        progressText.textContent = `${percentage}%`;
      });

      // Create hidden input with signed_id
      this.createHiddenInput(blob.signed_id);
      
      progressBar.style.display = 'none';
      fileItem.classList.add('uploaded');

    } catch (error) {
      console.error('Upload failed:', error);
      this.showError(`ファイル "${file.name}" のアップロードに失敗しました。`);
      fileItem.classList.add('error');
    }
  }

  async getDirectUploadUrl(file) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]');
    
    if (!csrfToken) {
      console.warn('CSRF token not found, direct upload may fail');
    }

    const headers = {
      'Content-Type': 'application/json'
    };
    
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken.content;
    }
    
    try {
      const response = await fetch('/rails/active_storage/direct_uploads', {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          blob: {
            filename: file.name,
            content_type: file.type,
            byte_size: file.size
          }
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Direct upload response error:', errorText);
        throw new Error(`Failed to get direct upload URL: ${response.status} ${errorText}`);
      }

      const data = await response.json();
      return data;
      
    } catch (error) {
      console.error('Error in getDirectUploadUrl:', error);
      throw error;
    }
  }

  async uploadToCloud(file, directUploadData, onProgress) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          onProgress(e.loaded / e.total);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 204) {
          resolve(directUploadData);
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'));
      });

      const formData = new FormData();
      Object.keys(directUploadData.direct_upload.fields).forEach(key => {
        formData.append(key, directUploadData.direct_upload.fields[key]);
      });
      formData.append('file', file);

      xhr.open('POST', directUploadData.direct_upload.url);
      xhr.send(formData);
    });
  }

  createHiddenInput(signedId) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = this.fileInput.name;
    input.value = signedId;
    input.classList.add('uploaded-file-input');
    this.element.appendChild(input);
  }

  addToFormData(file, fileItem) {
    // Store only drag-dropped files in custom property for form submission
    if (!this.fileInput.customFiles) {
      this.fileInput.customFiles = [];
    }
    this.fileInput.customFiles.push(file);
    
    // Mark as ready for form submission
    fileItem.classList.add('ready');
    fileItem.classList.add('drag-dropped');
    
    // Add a data attribute to track this file
    fileItem.dataset.fileName = file.name;
    fileItem.dataset.fileSize = file.size;
    
    // Set up form submission handler if not already done
    this.setupFormSubmissionHandler();
  }

  setupFormSubmissionHandler() {
    if (this.formHandlerSetup) return;
    
    const form = this.fileInput.closest('form');
    if (!form) {
      console.warn('No form found for file input');
      return;
    }
    
    // Store original form submission
    const originalSubmit = form.onsubmit;
    
    form.addEventListener('submit', (e) => {
      if (this.fileInput.customFiles && this.fileInput.customFiles.length > 0) {
        // Create a new DataTransfer object to merge files
        const dt = new DataTransfer();
        
        // Add existing files from file input (click-selected files)
        for (let i = 0; i < this.fileInput.files.length; i++) {
          dt.items.add(this.fileInput.files[i]);
        }
        
        // Add only custom files from drag and drop (not duplicates)
        this.fileInput.customFiles.forEach(file => {
          dt.items.add(file);
        });
        
        // Replace file input files with merged list
        this.fileInput.files = dt.files;
      }
      
      // Call original submit handler if it exists
      if (originalSubmit) {
        return originalSubmit.call(form, e);
      }
    });
    
    this.formHandlerSetup = true;
  }

  removeFile(fileItem) {
    // Remove corresponding hidden input if exists
    const hiddenInput = fileItem.querySelector('.uploaded-file-input');
    if (hiddenInput) {
      hiddenInput.remove();
    }
    
    fileItem.remove();
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  showError(message) {
    // Create or update error message container
    let errorContainer = this.element.querySelector('.upload-errors');
    if (!errorContainer) {
      errorContainer = document.createElement('div');
      errorContainer.className = 'upload-errors alert alert-danger';
      this.element.insertBefore(errorContainer, this.fileList);
    }
    
    errorContainer.innerHTML = `<i class="fa fa-exclamation-triangle"></i> ${message}`;
    errorContainer.style.display = 'block';
    
    // Auto-hide after 5 seconds
    setTimeout(() => {
      errorContainer.style.display = 'none';
    }, 5000);
  }
}

// Initialize drag drop uploads
function initializeDragDropUploads() {
  const elements = document.querySelectorAll('.drag-drop-upload');
  
  elements.forEach((element) => {
    // Skip if already initialized
    if (element.dataset.dragDropInitialized) {
      return;
    }
    
    const options = {
      sizeLimit: parseInt(element.dataset.sizeLimit) || window.MAX_FILE_SIZE_BYTES || 100000000,
      multiple: element.dataset.multiple !== 'false',
      directUpload: element.dataset.directUpload === 'true',
      acceptedTypes: element.dataset.acceptedTypes ? element.dataset.acceptedTypes.split(',') : null
    };
    
    try {
      new DragDropUpload(element, options);
      element.dataset.dragDropInitialized = 'true';
    } catch (error) {
      console.error('Error initializing drag drop upload:', error);
    }
  });
}

// Auto-initialize on Turbo load (Rails app uses Turbo)
document.addEventListener('turbo:load', initializeDragDropUploads);

// Fallback for non-Turbo environments
document.addEventListener('DOMContentLoaded', initializeDragDropUploads);

// Also try to initialize immediately if DOM is already loaded
if (document.readyState !== 'loading') {
  initializeDragDropUploads();
}